import { Box, Fade } from "@mui/material"
import { useParams } from "@tanstack/react-router"
import { type FC, useState } from "react"

import useCompanyCertificationMutation from "../hooks/useCompanyCertificationMutation"
import { getCertificationLabel } from "../utils"

import CancelDialog from "@/components/common/Modals/CancelDialog"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { type CompanyCertificationType, FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { NotificationType } from "@/schemas/notification"
import { COMPANY_EDIT_ROUTE } from "@/screens/Companies/constants"
import CertificationCard from "@/screens/Companies/edit/components/certification/components/CertificationCard"
import CompanyCertificationHeader from "@/screens/Companies/edit/components/certification/components/CompanyCertificationHeader"
import AddCertificationDialog from "@/screens/Companies/edit/components/certification/components/dialogs/AddCertificationDialog"
import EditCertificationDialog from "@/screens/Companies/edit/components/certification/components/dialogs/EditCertificationDialog"
import HistoricCertificatesDialog from "@/screens/Companies/edit/components/certification/components/dialogs/HistoricCertificatesDialog"
import PostCreationDialog from "@/screens/Companies/edit/components/certification/components/dialogs/PostCreationDialog"
import EmptyState from "@/screens/Companies/edit/components/certification/components/EmptyState"
import { useCertificationDialog } from "@/screens/Companies/edit/components/certification/hooks/useCertificationDialogs"
import useGetCompanyCertification from "@/screens/Companies/edit/components/certification/hooks/useGetCompanyCertification"
import {
  CertificationDialogType,
  type GroupedByCertificationType,
} from "@/screens/Companies/edit/components/certification/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const CompanyCertificationContainer: FC = () => {
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })

  const { locations, isLocationsFetching } = useGetLocations(companyId, {
    isLocationActive: true,
  })

  const { groupedCompanyCertifications, companyCertifications, fetching, executeQuery } = useGetCompanyCertification({
    companyId,
  })

  const { fileList, refetchFileUploadData } = useGetFileUploadData({
    referenceTableName: ReferenceTableName.Company,
    fieldGroups: [FieldGroupEnum.CompanyCertification],
  })

  const {
    setOpenedDialog,
    openedDialog,
    preSelectedLocations,
    preSelectedCertificationType,
    selectedCertification,
    setPreSelectedCertificationType,
    setPreSelectedLocations,
    setSelectedCertification,
    reset,
  } = useCertificationDialog()
  const { deleteBatchCertifications } = useCompanyCertificationMutation()
  const { enqueueNotification } = useNotificationsStore()

  const [idsToDelete, setIdsToDelete] = useState<string[]>([])
  const [selectedCertifications, setSelectedCertifications] = useState<
    Record<CompanyCertificationType, Record<string, boolean>>
  >({} as Record<CompanyCertificationType, Record<string, boolean>>)

  const hasNoLocations = !isLocationsFetching && !locations.length
  const shouldDisplayEmptyState = (!fetching && !groupedCompanyCertifications.length) || hasNoLocations

  const refreshData = () => {
    executeQuery()
    refetchFileUploadData()
  }

  const clearCertificationTypeSelection = (): void => {
    if (!preSelectedCertificationType) {
      return
    }

    setSelectedCertifications((state) => ({
      ...state,
      [preSelectedCertificationType]: {},
    }))
  }

  const handleCreateSuccess = () => {
    refreshData()
    clearCertificationTypeSelection()
    setOpenedDialog(CertificationDialogType.POST_CREATION)
  }

  const handleEditSuccess = () => {
    refreshData()
    reset()
  }

  const handleCancel = () => {
    reset()
  }

  const handleDeleteAllCertificationsConfirm = () => {
    setOpenedDialog(CertificationDialogType.CONFIRM_DELETE_ALL)
  }

  const handleDeleteSelectedCertificationsConfirm = (certificationIds: string[]) => {
    setIdsToDelete(certificationIds)
    setOpenedDialog(CertificationDialogType.CONFIRM_DELETE_SELECTED)
  }

  const handleDeleteAllCertifications = async () => {
    const companyCertificationIds = companyCertifications
      .filter(({ certificationType }) => certificationType === preSelectedCertificationType)
      .map(({ companyCertificationId }) => companyCertificationId) as string[]

    const { error } = await deleteBatchCertifications({
      input: {
        companyCertificationIds,
        companyId,
      },
    })

    if (error) {
      enqueueNotification({
        type: NotificationType.Error,
        message: `Failed to delete all ${getCertificationLabel(preSelectedCertificationType)} certificates`,
      })
    } else {
      enqueueNotification({
        type: NotificationType.Success,
        message: `All ${getCertificationLabel(preSelectedCertificationType)} certificates successfully deleted`,
      })

      clearCertificationTypeSelection()
      executeQuery()
    }

    reset()
  }

  const handleDeleteSelectedCertifications = async () => {
    const { error } = await deleteBatchCertifications({
      input: {
        companyCertificationIds: idsToDelete,
        companyId,
      },
    })

    if (error) {
      enqueueNotification({
        type: NotificationType.Error,
        message: `Failed to delete selected certificates`,
      })
    } else {
      enqueueNotification({
        type: NotificationType.Success,
        message: `Selected ${getCertificationLabel(preSelectedCertificationType)} certificates successfully deleted`,
      })

      clearCertificationTypeSelection()
      executeQuery()
    }

    reset()
    setIdsToDelete([])
  }

  const handleSetSelectedCertifications =
    (certification: GroupedByCertificationType) =>
    (selection: Record<string, boolean>): void =>
      setSelectedCertifications((state) => ({
        ...state,
        [certification.certificationType]: selection,
      }))

  return (
    <Box className='flex flex-col overflow-y-auto'>
      <Fade in={!isLocationsFetching && !fetching}>
        <Box className='my-6 flex justify-between'>
          {shouldDisplayEmptyState ? (
            <EmptyState hasNoLocations={hasNoLocations} setDialogOpen={setOpenedDialog} />
          ) : (
            <Box className='flex w-full flex-col'>
              <CompanyCertificationHeader setDialogOpen={setOpenedDialog} />

              <Box className='mt-6 h-full flex-col overflow-y-auto'>
                {/* Certification Card  */}
                {groupedCompanyCertifications.map((certification, index) => (
                  <CertificationCard
                    key={"certification-list-" + index}
                    locations={locations}
                    groupedCertification={certification}
                    fileList={fileList}
                    selectedCertifications={selectedCertifications[certification.certificationType] || {}}
                    setSelectedCertifications={handleSetSelectedCertifications(certification)}
                    setOpenedDialog={setOpenedDialog}
                    setPreSelectedCertificationType={setPreSelectedCertificationType}
                    setPreSelectedLocations={setPreSelectedLocations}
                    setSelectedCertification={setSelectedCertification}
                    handleDeleteAllCertifications={handleDeleteAllCertificationsConfirm}
                    handleDeleteSelectedCertifications={handleDeleteSelectedCertificationsConfirm}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Fade>

      {openedDialog === CertificationDialogType.ADD_CERTIFICATION && (
        <AddCertificationDialog
          companyId={companyId}
          certificationType={preSelectedCertificationType || undefined}
          locations={preSelectedLocations || undefined}
          onClose={handleCancel}
          onSuccess={handleCreateSuccess}
        />
      )}

      {openedDialog === CertificationDialogType.EDIT_CERTIFICATION && selectedCertification && (
        <EditCertificationDialog
          companyId={companyId}
          certification={selectedCertification}
          onClose={handleCancel}
          onSuccess={handleEditSuccess}
        />
      )}

      {openedDialog === CertificationDialogType.HISTORIC_CERTIFICATES && preSelectedCertificationType && (
        <HistoricCertificatesDialog
          companyId={companyId}
          certificationType={preSelectedCertificationType}
          onClose={handleCancel}
        />
      )}

      {openedDialog === CertificationDialogType.POST_CREATION && (
        <PostCreationDialog
          onClose={handleCancel}
          onSuccess={() => setOpenedDialog(CertificationDialogType.ADD_CERTIFICATION)}
        />
      )}

      {openedDialog === CertificationDialogType.CONFIRM_DELETE_ALL && (
        <CancelDialog
          title={`Are you sure you want to delete all ${getCertificationLabel(preSelectedCertificationType)} certificates?`}
          message={`This action will delete all ${getCertificationLabel(preSelectedCertificationType)} certificates.`}
          confirmLabel='Yes, Delete All'
          closeLabel='Cancel'
          onClose={handleCancel}
          onConfirm={handleDeleteAllCertifications}
        />
      )}

      {openedDialog === CertificationDialogType.CONFIRM_DELETE_SELECTED && (
        <CancelDialog
          title={`Are you sure you want to delete the selected ${getCertificationLabel(preSelectedCertificationType)} certificates?`}
          message={`This action will delete the selected ${getCertificationLabel(preSelectedCertificationType)} certificates.`}
          confirmLabel='Yes, Delete'
          closeLabel='Cancel'
          onClose={handleCancel}
          onConfirm={handleDeleteSelectedCertifications}
        />
      )}
    </Box>
  )
}

export default CompanyCertificationContainer
